// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the pin graph as a window over the globe (owner's ask,
 * 2026-10-04): dragged out of the Tools panel's Data tab, or popped out
 * with its button, it lands on the canvas, where its title bar moves it
 * and its corner resizes it. ✕, its "back to Tools" button, or a drop on
 * the Tools panel or the Tools button puts it back where it came from.
 *
 *   ┌ ⠿ Pin graph ──────── ⇲  ✕ ┐  ← drag the bar; ⠿ moves with the arrow keys
 *   │   (the graph, drawn at     │
 *   │    this window's size)     │
 *   │ ● Pin 1  281 K   ● Pin 2 … │
 *   └─────────────────────────── ◢┘  ← drag, or the arrow keys, to resize
 *
 * Every move between the panel and the canvas is animated (a flight from
 * one rectangle to the other) unless the viewer asks for reduced motion.
 * Where the window last sat and how big it was are remembered per browser.
 *
 * This module only holds the window; what is drawn in it, and the Tools
 * panel's side of it, are `dataPinsUI.ts`'s.
 */

import { t, tAttr } from '../i18n'

export type DockReason = 'drop' | 'button' | 'close'

export interface PinGraphWindowDeps {
  /** AYNI: download the pins' series as CSV (pinsCsv.ts); without it, no button. */
  download?(): void
  /** Draw the graph into `body` at this size (CSS px), and the legend into `foot`. */
  draw(body: HTMLElement, foot: HTMLElement, width: number, height: number): void
  /**
   * Where a drop puts it back, in order of preference: the Tools panel
   * when open, then the Tools button. Only visible ones count.
   */
  dockTargets(): HTMLElement[]
  /**
   * The rectangle it flies into on the way back: the graph's place in the
   * Tools panel when that is on screen, else the Tools button.
   * `reason` lets the caller open the panel first (the ⇲ button does).
   */
  dockRect(reason: DockReason, target: HTMLElement | null): DOMRect | null
  /** It has gone back; the panel draws the graph again. */
  onDocked(reason: DockReason): void
  /** A graph carried out of the panel was let go over the globe. */
  onLanded?(): void
}

export interface PinGraphWindow {
  readonly el: HTMLElement
  /** Follow a pointer that grabbed the graph in the panel at `from`. */
  carry(ev: PointerEvent, from: DOMRect): void
  /** Fly out of `from` to where it last sat, with no drag. */
  popOut(from: DOMRect): void
  redraw(): void
  dock(reason: DockReason): void
}

interface Rect { x: number; y: number; w: number; h: number }

const STORAGE_KEY = 'ayni-pin-graph-rect'
const MARGIN = 8
const MIN_W = 240
const MIN_H = 170
const FLY_MS = 420
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

const ICON_DOWNLOAD = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M8 2.5v7.5M4.8 7l3.2 3.2L11.2 7" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 12.5h10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'

const ICON_DOCK = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 3h10v10H3z" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M6 6l5 5M11 7.5V11H7.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'

function reducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

function uiScale(): number {
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale'))
  return Number.isFinite(v) && v > 0 ? v : 1
}

function viewport(): { w: number; h: number } {
  return { w: window.innerWidth, h: window.innerHeight }
}

/** Keep the whole window on screen, no smaller than the minimum. */
export function clampRect(r: Rect, vw: number, vh: number): Rect {
  const w = Math.max(Math.min(MIN_W, vw - 2 * MARGIN), Math.min(r.w, vw - 2 * MARGIN))
  const h = Math.max(Math.min(MIN_H, vh - 2 * MARGIN), Math.min(r.h, vh - 2 * MARGIN))
  const x = Math.min(Math.max(MARGIN, r.x), vw - w - MARGIN)
  const y = Math.min(Math.max(MARGIN, r.y), vh - h - MARGIN)
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) }
}

function remembered(): Rect | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const r = JSON.parse(raw) as Partial<Rect>
    if ([r.x, r.y, r.w, r.h].every((n) => typeof n === 'number' && Number.isFinite(n))) return r as Rect
  } catch {
    // storage refused or garbled: the default place
  }
  return null
}

function remember(r: Rect): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(r))
  } catch {
    // storage refused: it is forgotten with the page
  }
}

/** A comfortable size: wider than the panel's, about a third of a desktop screen. */
function preferredSize(): { w: number; h: number } {
  const s = uiScale()
  const { w, h } = viewport()
  return { w: Math.min(460 * s, w - 2 * MARGIN), h: Math.min(290 * s, Math.max(MIN_H, h * 0.5)) }
}

/** The open Tools panel's rectangle, if it is open. */
function toolsPanelRect(): DOMRect | null {
  const popover = document.getElementById('tools-menu-popover')
  const r = popover && !popover.classList.contains('hidden') ? popover.getBoundingClientRect() : null
  return r && r.width ? r : null
}

/** Where a popped-out graph goes the first time: top centre of the room the Tools panel leaves. */
function defaultRect(): Rect {
  const { w: vw, h: vh } = viewport()
  const size = preferredSize()
  const room = toolsPanelRect()?.left ?? vw
  return clampRect({ x: (room - size.w) / 2, y: 64 * uiScale(), ...size }, vw, vh)
}

/** Out of the open Tools panel's way, where the screen leaves room beside it. */
function besidePanel(r: Rect): Rect {
  const panel = toolsPanelRect()
  if (!panel || r.x + r.w <= panel.left || r.x >= panel.right) return r
  return { ...r, x: panel.left - r.w - 16 }
}

function inside(r: DOMRect, x: number, y: number): boolean {
  return r.width > 0 && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
}

/**
 * Animate `el` (at `to`) as if it were coming from `from`, or (with
 * `reverse`) going from where it is to `from`. Resolves when done, at
 * once under reduced motion or where the Web Animations API is missing.
 */
function fly(el: HTMLElement, from: DOMRect | Rect, to: Rect, opts: { reverse?: boolean; fade?: boolean } = {}): Promise<void> {
  if (reducedMotion() || typeof el.animate !== 'function') return Promise.resolve()
  const fx = 'left' in from ? from.left : from.x
  const fy = 'top' in from ? from.top : from.y
  const fw = 'width' in from ? from.width : from.w
  const fh = 'height' in from ? from.height : from.h
  const away = `translate(${fx - to.x}px, ${fy - to.y}px) scale(${Math.max(0.05, fw / to.w)}, ${Math.max(0.05, fh / to.h)})`
  const frames: Keyframe[] = [
    { transform: away, opacity: opts.fade ? 0 : 0.6 },
    { transform: 'translate(0, 0) scale(1, 1)', opacity: 1 },
  ]
  if (opts.reverse) frames.reverse()
  // Held at the end only on the way back, where the window is removed next.
  const anim = el.animate(frames, { duration: FLY_MS, easing: EASE, fill: opts.reverse ? 'forwards' : 'none' })
  return anim.finished.then(() => undefined, () => undefined)
}

export function createPinGraphWindow(deps: PinGraphWindowDeps): PinGraphWindow {
  const el = document.createElement('section')
  el.className = 'pin-graph-window'
  el.setAttribute('role', 'dialog')
  el.setAttribute('aria-modal', 'false')
  el.setAttribute('aria-labelledby', 'pin-graph-window-title')
  el.innerHTML = `
    <header class="pin-graph-head">
      <button type="button" class="pin-graph-grip" id="pin-graph-grip" aria-label="${tAttr('tools.data.graph.move')}" title="${tAttr('tools.data.graph.move')}">⠿</button>
      <h2 class="pin-graph-title" id="pin-graph-window-title">${t('tools.data.graph.title')}</h2>
      ${deps.download ? `<button type="button" class="pin-graph-btn" id="pin-graph-csv" aria-label="${tAttr('tools.data.csv.aria')}" title="${tAttr('tools.data.csv.aria')}">${ICON_DOWNLOAD}</button>` : ''}
      <button type="button" class="pin-graph-btn" id="pin-graph-dock" aria-label="${tAttr('tools.data.graph.dock')}" title="${tAttr('tools.data.graph.dock')}">${ICON_DOCK}</button>
      <button type="button" class="pin-graph-btn" id="pin-graph-close" aria-label="${tAttr('tools.data.graph.close')}" title="${tAttr('tools.data.graph.close')}">✕</button>
    </header>
    <div class="pin-graph-body" id="pin-graph-body"></div>
    <div class="pin-graph-foot" id="pin-graph-foot"></div>
    <button type="button" class="pin-graph-resize" id="pin-graph-resize" aria-label="${tAttr('tools.data.graph.resize')}" title="${tAttr('tools.data.graph.resize')}"></button>`
  const body = el.querySelector<HTMLElement>('.pin-graph-body')!
  const foot = el.querySelector<HTMLElement>('.pin-graph-foot')!
  const grip = el.querySelector<HTMLButtonElement>('.pin-graph-grip')!
  const resizer = el.querySelector<HTMLButtonElement>('.pin-graph-resize')!

  let rect: Rect = defaultRect()
  let gone = false
  let busy = false
  let drawQueued = false

  function place(r: Rect): void {
    rect = r
    el.style.left = `${r.x}px`
    el.style.top = `${r.y}px`
    el.style.width = `${r.w}px`
    el.style.height = `${r.h}px`
  }

  function redraw(): void {
    if (gone) return
    deps.draw(body, foot, body.clientWidth || rect.w, body.clientHeight || Math.max(80, rect.h - 70))
  }

  function queueDraw(): void {
    if (drawQueued) return
    drawQueued = true
    requestAnimationFrame(() => {
      drawQueued = false
      redraw()
    })
  }

  /** The dock target under the pointer, if any, lit up. */
  let lit: HTMLElement | null = null
  function hover(x: number, y: number): HTMLElement | null {
    const target = deps.dockTargets().find((d) => inside(d.getBoundingClientRect(), x, y)) ?? null
    if (target !== lit) {
      lit?.classList.remove('pin-graph-dock-target')
      target?.classList.add('pin-graph-dock-target')
      lit = target
    }
    el.classList.toggle('over-dock', !!target)
    return target
  }

  /**
   * One drag, from the bar or from the panel: follows the pointer by its
   * grab offset, lights up a dock target under it, and on release either
   * docks or stays where it was let go. (What it is grabbed by is
   * `touch-action: none`, so a finger never scrolls the page instead.)
   */
  function drag(pointerId: number, grabX: number, grabY: number, onLand: () => void): void {
    el.classList.add('carried')
    let lastX = grabX + rect.x
    let lastY = grabY + rect.y
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      lastX = e.clientX
      lastY = e.clientY
      const { w: vw, h: vh } = viewport()
      // Free while carried (it may go over the panel); clamped where it lands.
      place({ ...rect, x: Math.min(Math.max(-rect.w + 40, e.clientX - grabX), vw - 40), y: Math.min(Math.max(0, e.clientY - grabY), vh - 30) })
      hover(e.clientX, e.clientY)
    }
    const end = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', end)
      document.removeEventListener('pointercancel', end)
      el.classList.remove('carried')
      const target = e.type === 'pointerup' ? hover(lastX, lastY) : null
      lit?.classList.remove('pin-graph-dock-target')
      lit = null
      el.classList.remove('over-dock')
      if (target) {
        void dockTo('drop', target)
        return
      }
      onLand()
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', end)
    document.addEventListener('pointercancel', end)
  }

  function settle(): void {
    const { w: vw, h: vh } = viewport()
    place(clampRect(rect, vw, vh))
    remember(rect)
    queueDraw()
  }

  async function dockTo(reason: DockReason, target: HTMLElement | null): Promise<void> {
    if (gone || busy) return
    busy = true
    const hadFocus = el.contains(document.activeElement)
    // Where it last sat was remembered when it settled there, not where it was dropped back.
    const to = deps.dockRect(reason, target)
    if (to) {
      // Into the button it shrinks away; into the panel it lands whole.
      const intoButton = to.width < 80 || to.height < 60
      el.classList.add('docking')
      await fly(el, to, rect, { reverse: true, fade: intoButton })
    }
    gone = true
    lit?.classList.remove('pin-graph-dock-target')
    window.removeEventListener('resize', onResize)
    resizeObserver?.disconnect()
    el.remove()
    deps.onDocked(reason)
    if (hadFocus && !document.activeElement?.isConnected) document.getElementById('tools-menu-toggle')?.focus()
  }

  const onResize = () => settle()
  window.addEventListener('resize', onResize)
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => queueDraw()) : null
  resizeObserver?.observe(body)

  // Move by the bar (not its buttons).
  el.querySelector('.pin-graph-head')!.addEventListener('pointerdown', (ev) => {
    const e = ev as PointerEvent
    if (busy || e.button > 0 || (e.target as HTMLElement).closest('.pin-graph-btn')) return
    e.preventDefault()
    drag(e.pointerId, e.clientX - rect.x, e.clientY - rect.y, settle)
  })

  // Resize by the corner.
  resizer.addEventListener('pointerdown', (e) => {
    if (busy || e.button > 0) return
    e.preventDefault()
    e.stopPropagation()
    const start = { x: e.clientX, y: e.clientY, w: rect.w, h: rect.h }
    el.classList.add('resizing')
    const move = (m: PointerEvent) => {
      if (m.pointerId !== e.pointerId) return
      const { w: vw, h: vh } = viewport()
      place({
        ...rect,
        w: Math.min(Math.max(MIN_W, start.w + m.clientX - start.x), vw - rect.x - MARGIN),
        h: Math.min(Math.max(MIN_H, start.h + m.clientY - start.y), vh - rect.y - MARGIN),
      })
      queueDraw()
    }
    const end = (u: PointerEvent) => {
      if (u.pointerId !== e.pointerId) return
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', end)
      document.removeEventListener('pointercancel', end)
      el.classList.remove('resizing')
      settle()
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', end)
    document.addEventListener('pointercancel', end)
  })

  // The keyboard's way: arrows on ⠿ move it, on ◢ resize it; Shift for bigger steps.
  const arrows = (resize: boolean) => (e: KeyboardEvent) => {
    const step = e.shiftKey ? 48 : 12
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key]
    if (!d) return
    e.preventDefault()
    e.stopPropagation()
    place(resize ? { ...rect, w: rect.w + d[0], h: rect.h + d[1] } : { ...rect, x: rect.x + d[0], y: rect.y + d[1] })
    settle()
  }
  grip.addEventListener('keydown', arrows(false))
  resizer.addEventListener('keydown', arrows(true))

  el.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    void dockTo('close', null)
  })
  el.querySelector('#pin-graph-csv')?.addEventListener('click', (e) => {
    e.stopPropagation()
    deps.download?.()
  })
  el.querySelector('#pin-graph-dock')!.addEventListener('click', (e) => {
    e.stopPropagation()
    void dockTo('button', null)
  })
  el.querySelector('#pin-graph-close')!.addEventListener('click', (e) => {
    e.stopPropagation()
    void dockTo('close', null)
  })
  // A click in here is not a click outside the Tools panel.
  el.addEventListener('click', (e) => e.stopPropagation())

  document.body.appendChild(el)

  return {
    el,
    carry(ev, from) {
      // Held with its graph where and as big as it was in the panel (the
      // bar and legend around it); it grows where it lands. A tall first
      // placing gives the body room to be measured against.
      place({ x: from.left, y: from.top, w: from.width, h: from.height + 400 })
      redraw()
      const aroundW = rect.w - body.clientWidth
      const aroundH = rect.h - body.clientHeight
      place({ x: from.left - body.offsetLeft, y: from.top - body.offsetTop, w: from.width + aroundW, h: from.height + aroundH })
      redraw()
      el.classList.add('lifted')
      drag(ev.pointerId, ev.clientX - rect.x, ev.clientY - rect.y, () => {
        const small = rect
        const size = remembered() ?? preferredSize()
        const { w: vw, h: vh } = viewport()
        place(clampRect({ x: small.x, y: small.y, w: size.w, h: size.h }, vw, vh))
        el.classList.remove('lifted')
        redraw()
        deps.onLanded?.()
        void fly(el, small, rect).then(settle)
      })
    },
    popOut(from) {
      const { w: vw, h: vh } = viewport()
      const was = remembered()
      place(was ? clampRect(besidePanel(clampRect(was, vw, vh)), vw, vh) : defaultRect())
      redraw()
      void fly(el, from, rect, { fade: true }).then(settle)
      el.querySelector<HTMLButtonElement>('#pin-graph-dock')?.focus({ preventScroll: true })
    },
    redraw: queueDraw,
    dock: (reason) => void dockTo(reason, null),
  }
}
